import React from 'react';

import { Box, Skeleton, Stack, useTheme } from '@chakra-ui/react';
import { OKRCardComponent } from 'components/RabbitOKRSidePanel/Components/OKRCardComponent';
import { resetKR } from 'components/RabbitOKRSidePanel/KRslice';
import { useOKRTagsQuery } from 'components/RabbitOKRSidePanel/datahooks/useGetCustomTagsOrganization';
import useUnsavedDataAlert from 'components/hooks/useUnsavedDataAlert';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { kRDrawerSelector } from 'selectors/kRDrawer';

import { OKR_TYPES } from 'constants/ProjectConstants';
import { getEncodedPanelStack, getParsedPanelStack, getQueryParams } from 'constants/helper';

import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { checkDetailsModified } from '../ChangeDetails/ChangeDetails';
import { checkCheckInValuesModified } from '../CheckIn/CheckIn';
import { NoSupportedBy } from './NoSupportedBy';

export const SupportedByOKRs = ({ data, isLoading, isError, error, isOKRPanelDisabled }) => {
  const history = useHistory();
  const theme = useTheme();
  const dispatch = useDispatch();
  const okrDetails = useSelector(kRDrawerSelector);
  const { uuid, type: parentType, localCheckInStateValues, localStateValues } = okrDetails;
  const { showUnsavedDataAlertDialog } = useUnsavedDataAlert();
  const { data: customTags = [] } = useOKRTagsQuery({ okrId: uuid });

  const isCheckInValuesModified = checkCheckInValuesModified(localCheckInStateValues, okrDetails);

  const isCommentModified = localCheckInStateValues?.isCommentModified || false;
  const isDetailsChanged = checkDetailsModified(localStateValues, okrDetails, customTags);

  const isUnsavedChangesInOKR = isCheckInValuesModified || isCommentModified || isDetailsChanged;

  const handleOnClick = (uuidToOpen, type) => {
    if (uuidToOpen === uuid) return;

    const otherQueryParams = getQueryParams(history.location.search);
    const parserPanelStack = getParsedPanelStack({ location: history.location });

    const isObjective = type === OKR_TYPES.OBJECTIVE;

    const overriddenQueryParams = {
      ...otherQueryParams,
      /* Following is done to clear any residual query params */
      objectivePId: null,
      ids: null,
      showTaskDetails: null,
      empId: null,
      type: null,
    };

    if (isObjective) {
      overriddenQueryParams.objectivePId = uuidToOpen;
    } else {
      overriddenQueryParams.ids = [uuidToOpen];
      overriddenQueryParams.showTaskDetails = true;
      overriddenQueryParams.type = 'GOAL';
    }

    const routeWithPanelStack = getEncodedPanelStack({
      panelStack: parserPanelStack ?? [],
      type: parentType === 'OBJECTIVE' ? 'OBJECTIVE' : 'GOAL',
      objectivePId: parentType === 'OBJECTIVE' ? uuid : null,
      id: parentType === 'OBJECTIVE' ? null : uuid,
      otherQueryParams: overriddenQueryParams,
    });

    history.push({
      search: routeWithPanelStack,
    });
  };

  const onChildClick = (...props) => {
    if (isUnsavedChangesInOKR) {
      showUnsavedDataAlertDialog({
        onCancelCallback: () => {
          dispatch(resetKR());
          handleOnClick(...props);
        },
      });
    } else {
      dispatch(resetKR());

      handleOnClick(...props);
    }
  };

  if (isLoading) {
    return (
      <Stack>
        <Skeleton h="16px" />
        <Skeleton h="16px" />
        <Skeleton h="16px" />
      </Stack>
    );
  }

  const isEmpty = data?.length === 0;
  if (isEmpty) {
    return (
      <Box px={6} py={2}>
        <NoSupportedBy isOKRPanelDisabled={isOKRPanelDisabled} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box h="100px">
        <TextBodyMedium color={theme.colors.alerts.negative}>{error.message ?? 'Some error occurred.'}</TextBodyMedium>
      </Box>
    );
  }

  return (
    <Box sx={{ '> *': { marginTop: '4px' } }}>
      {data.map((okr) => {
        const { uuid, type } = okr;
        return <OKRCardComponent key={uuid} level={0} {...okr} onClick={() => onChildClick(uuid, type)} />;
      })}
    </Box>
  );
};
